import { useMemo } from "react";

const EARTH_RADIUS_FEET = 20902231;
const SQFT_PER_ACRE = 43560;

function safeParseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractPolygons(geometry) {
  if (!geometry?.coordinates) return [];
  if (geometry.type === "Polygon") return [geometry.coordinates];
  if (geometry.type === "MultiPolygon") return geometry.coordinates;
  return [];
}

function computeBounds(points, size, padding) {
  if (!points.length) return null;
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const widthSpan = Math.max(maxX - minX, 0.0001);
  const heightSpan = Math.max(maxY - minY, 0.0001);
  const availableWidth = size.width - padding * 2;
  const availableHeight = size.height - padding * 2;
  const scale = Math.min(availableWidth / widthSpan, availableHeight / heightSpan);
  const offsetX = padding + (availableWidth - widthSpan * scale) / 2;
  const offsetY = padding + (availableHeight - heightSpan * scale) / 2;
  return { minX, maxY, scale, offsetX, offsetY };
}

function projectCoordinate([longitude, latitude], bounds) {
  const { minX, maxY, scale, offsetX, offsetY } = bounds;
  return {
    x: (longitude - minX) * scale + offsetX,
    y: (maxY - latitude) * scale + offsetY,
  };
}

function buildPaths(geometry, bounds) {
  if (!bounds) return [];
  return extractPolygons(geometry)
    .map((polygon) => {
      const ring = polygon?.[0];
      if (!ring?.length) return null;
      const path = ring
        .map((coordinate, index) => {
          const point = projectCoordinate(coordinate, bounds);
          if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return "";
          return `${index === 0 ? "M" : "L"}${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
        })
        .filter(Boolean)
        .join(" ");
      return path ? `${path} Z` : null;
    })
    .filter(Boolean);
}

function getOuterRings(geometry) {
  return extractPolygons(geometry)
    .map((polygon) => polygon?.[0])
    .filter((ring) => Array.isArray(ring) && ring.length >= 3);
}

function getRingPoints(ring) {
  const isClosed =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1];
  return isClosed ? ring.slice(0, -1) : ring;
}

function toLocalFeet([longitude, latitude], referenceLatitude) {
  const latitudeRadians = (Number(latitude) * Math.PI) / 180;
  const longitudeRadians = (Number(longitude) * Math.PI) / 180;
  const referenceRadians = (referenceLatitude * Math.PI) / 180;

  return {
    x: EARTH_RADIUS_FEET * longitudeRadians * Math.cos(referenceRadians),
    y: EARTH_RADIUS_FEET * latitudeRadians,
  };
}

export function measureParcelGeometry(geometry) {
  const rings = getOuterRings(geometry);
  const coordinates = rings.flatMap(getRingPoints);
  if (!coordinates.length) {
    return { areaSqft: 0, perimeterFeet: 0, edges: [] };
  }

  const referenceLatitude =
    coordinates.reduce((sum, coordinate) => sum + Number(coordinate[1]), 0) /
    coordinates.length;
  let areaSqft = 0;
  let perimeterFeet = 0;
  const edges = [];

  rings.forEach((ring) => {
    const points = getRingPoints(ring);
    const absoluteLocalPoints = points.map((point) =>
      toLocalFeet(point, referenceLatitude),
    );
    const origin = absoluteLocalPoints[0];
    const localPoints = absoluteLocalPoints.map((point) => ({
      x: point.x - origin.x,
      y: point.y - origin.y,
    }));

    let signedArea = 0;
    localPoints.forEach((point, index) => {
      const next = localPoints[(index + 1) % localPoints.length];
      signedArea += point.x * next.y - next.x * point.y;
    });
    areaSqft += Math.abs(signedArea) / 2;

    points.forEach((start, index) => {
      const end = points[(index + 1) % points.length];
      const localStart = toLocalFeet(start, referenceLatitude);
      const localEnd = toLocalFeet(end, referenceLatitude);
      const lengthFeet = Math.hypot(
        localEnd.x - localStart.x,
        localEnd.y - localStart.y,
      );

      perimeterFeet += lengthFeet;
      edges.push({
        lengthFeet,
        start,
        end,
      });
    });
  });

  return {
    areaSqft,
    areaAcres: areaSqft / SQFT_PER_ACRE,
    perimeterFeet,
    edges,
  };
}

function measureGeometry(geometry, bounds) {
  const measurements = measureParcelGeometry(geometry);
  return {
    ...measurements,
    edges: measurements.edges.map((edge) => {
      const projectedStart = projectCoordinate(edge.start, bounds);
      const projectedEnd = projectCoordinate(edge.end, bounds);
      return {
        ...edge,
        x: (projectedStart.x + projectedEnd.x) / 2,
        y: (projectedStart.y + projectedEnd.y) / 2,
      };
    }),
  };
}

function buildBlueprint(parcelGeometry, buildingGeometries) {
  const parcelPolygons = extractPolygons(parcelGeometry);
  const buildingPolygons = buildingGeometries.flatMap(extractPolygons);
  const allPoints = [...parcelPolygons, ...buildingPolygons].flatMap((polygon) =>
    polygon.flatMap((ring) => ring),
  );
  const size = { width: 340, height: 200 };
  const bounds = computeBounds(allPoints, size, 30);
  const measurements = bounds
    ? measureGeometry(parcelGeometry, bounds)
    : { areaSqft: 0, perimeterFeet: 0, edges: [] };

  return {
    size,
    parcelPaths: buildPaths(parcelGeometry, bounds),
    buildingPaths: buildingGeometries
      .map((geometry) => buildPaths(geometry, bounds))
      .flat(),
    ...measurements,
  };
}

function formatNumber(value, digits = 0) {
  if (value === null || value === undefined) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: digits,
  }).format(number);
}

export function BlueprintPreview({
  parcelGeometry,
  buildingGeometries = [],
}) {
  const blueprint = useMemo(
    () => buildBlueprint(parcelGeometry, buildingGeometries),
    [parcelGeometry, buildingGeometries],
  );
  const hasGeometry =
    blueprint.parcelPaths.length || blueprint.buildingPaths.length;

  return (
    <div className="rounded-lg border border-gray-100 bg-slate-50 p-2">
      {hasGeometry ? (
        <>
          <svg
            width="100%"
            height="200"
            viewBox={`0 0 ${blueprint.size.width} ${blueprint.size.height}`}
          >
            {blueprint.parcelPaths.map((path, index) => (
              <path
                key={`parcel-${index}`}
                d={path}
                className="fill-sky-100 stroke-sky-500 stroke-[1.5]"
              />
            ))}
            {blueprint.buildingPaths.map((path, index) => (
              <path
                key={`building-${index}`}
                d={path}
                className="fill-emerald-200 stroke-emerald-600 stroke-[1]"
              />
            ))}
            {blueprint.edges.map((edge, index) => (
              <text
                key={`edge-${index}`}
                x={edge.x}
                y={edge.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-slate-700 text-[8px] font-semibold"
                style={{
                  paintOrder: "stroke",
                  stroke: "white",
                  strokeWidth: 4,
                  strokeLinejoin: "round",
                }}
              >
                {`${index + 1} · ${formatNumber(edge.lengthFeet)} ft`}
              </text>
            ))}
          </svg>

          <div className="space-y-2 border-t border-slate-200 pt-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[9px] uppercase tracking-wide text-gray-500">
                  Área
                </div>
                <div className="text-xs font-semibold text-gray-900">
                  {formatNumber(blueprint.areaSqft / SQFT_PER_ACRE, 3)} ac
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wide text-gray-500">
                  Área
                </div>
                <div className="text-xs font-semibold text-gray-900">
                  {formatNumber(blueprint.areaSqft)} ft²
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wide text-gray-500">
                  Perímetro
                </div>
                <div className="text-xs font-semibold text-gray-900">
                  {formatNumber(blueprint.perimeterFeet)} ft
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {blueprint.edges.map((edge, index) => (
                <span
                  key={`edge-summary-${index}`}
                  className="rounded-md bg-white px-2 py-1 text-[10px] text-gray-600 shadow-sm"
                >
                  Face {index + 1}: {formatNumber(edge.lengthFeet)} ft
                </span>
              ))}
            </div>

            <p className="text-[9px] leading-4 text-gray-400">
              Medidas aproximadas calculadas a partir da geometria do Regrid.
            </p>
          </div>
        </>
      ) : (
        <div className="flex h-40 items-center justify-center text-xs text-gray-500">
          Geometria indisponível
        </div>
      )}
    </div>
  );
}

export function BuildingCard({ building }) {
  const footprint = formatNumber(building.footprint_sqft);
  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">
          {footprint ? `${footprint} sqft` : "Footprint"}
        </span>
        {building.is_largest && (
          <span className="text-[11px] font-medium text-emerald-600">Maior</span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-gray-500">
        {building.source_name && `${building.source_name} • `}
        {building.source_date_raw}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div>
          Lat{" "}
          <span className="text-sm font-semibold text-gray-900">
            {building.centroid_latitude ?? "—"}
          </span>
        </div>
        <div>
          Lon{" "}
          <span className="text-sm font-semibold text-gray-900">
            {building.centroid_longitude ?? "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

export { safeParseJson };
